import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API } from '../helpers';
import './RegistrationPuzzleV108.css';

export default function RegistrationPuzzleV108({
  onChange,
  revision = 0,
  purpose = 'registration',
}) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const text = (cn, en) => (zh ? cn : en);
  const [puzzle, setPuzzle] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [position, setPosition] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [expires, setExpires] = useState(0);
  const generation = useRef(0);
  const pending = useRef(false);
  const currentPosition = useRef(0);
  const maximumPosition = useRef(256);
  const drag = useRef(null);
  const picture = useRef(null);
  const endpoint =
    purpose === 'login' ? '/api/login/captcha' : '/api/registration/captcha';
  const notify = useRef(onChange);
  notify.current = onChange;

  const move = useCallback((value) => {
    const next = Math.max(
      0,
      Math.min(maximumPosition.current, Math.round(value))
    );
    currentPosition.current = next;
    setPosition(next);
  }, []);

  useEffect(() => {
    const run = ++generation.current;
    const abort = new AbortController();
    pending.current = false;
    drag.current = null;
    notify.current('');
    setPhase('loading');
    setPuzzle(null);
    setExpires(0);
    move(0);
    Promise.resolve()
      .then(() => API.post(endpoint, {}, { signal: abort.signal }))
      .then(({ data }) => {
        if (generation.current !== run) return;
        const item = data?.data;
        const rotating = item?.kind === 'rotate';
        if (
          !data?.success ||
          !item?.id ||
          !item?.background ||
          !item?.piece ||
          item.width !== 320 ||
          item.height !== 180 ||
          !['slide', 'rotate', undefined].includes(item.kind) ||
          item.piece_size !== (rotating ? 144 : 64) ||
          (rotating && (item.piece_x !== 88 || item.piece_y !== 18)) ||
          !Number.isFinite(item.piece_y) ||
          item.piece_y < 0 ||
          item.piece_y + item.piece_size > 180 ||
          !Number.isFinite(item.expires_at) ||
          item.expires_at <= Date.now()
        ) {
          throw new Error('Invalid puzzle');
        }
        maximumPosition.current = rotating ? 360 : 256;
        setPuzzle(item);
        setExpires(item.expires_at);
        setPhase('ready');
      })
      .catch((error) => {
        if (generation.current === run && !abort.signal.aborted)
          setPhase(error?.response?.status === 429 ? 'limited' : 'error');
      });
    return () => {
      generation.current++;
      abort.abort();
    };
  }, [revision, refresh, move, endpoint]);

  useEffect(() => {
    if (!expires) return;
    const timer = setTimeout(() => {
      generation.current++;
      pending.current = false;
      drag.current = null;
      notify.current('');
      setPhase('expired');
    }, Math.max(0, expires - Date.now()));
    return () => clearTimeout(timer);
  }, [expires]);

  const verify = async () => {
    if (!puzzle || phase !== 'ready' || pending.current) return;
    const run = generation.current;
    pending.current = true;
    setPhase('checking');
    try {
      const { data } = await API.post(`${endpoint}/verify`, {
        id: puzzle.id,
        position: currentPosition.current,
      });
      if (generation.current !== run) return;
      if (
        !data?.success ||
        !data?.data?.token ||
        !Number.isFinite(data.data.expires_at) ||
        data.data.expires_at <= Date.now()
      )
        throw new Error('Verification failed');
      setPhase('verified');
      setExpires(data.data.expires_at);
      notify.current(data.data.token);
    } catch (error) {
      if (generation.current === run) {
        notify.current('');
        setExpires(0);
        setPhase(error?.response?.status === 429 ? 'limited' : 'failed');
      }
    } finally {
      if (generation.current === run) pending.current = false;
    }
  };

  const rotating = puzzle?.kind === 'rotate';
  const expired = phase === 'expired';
  const retryLabel = text('重新验证', 'Verify again');
  const expiryMessage = text(
    '人类验证超时，请点击重新验证',
    'Human verification timed out. Click to verify again.'
  );
  const labels = {
    loading: text('加载中', 'Loading'),
    ready: rotating
      ? text('旋转拼图', 'Rotation puzzle')
      : text('滑块拼图', 'Slide puzzle'),
    checking: text('验证中', 'Verifying'),
    verified: text('验证通过', 'Verified'),
    failed: text('验证失败，请换图重试', 'Not matched. Refresh to retry.'),
    error: text('图片加载失败，请重试', 'Could not load. Retry.'),
    limited: text('请求过于频繁，请稍后再试', 'Too many requests. Try later.'),
    expired: text('人类验证超时', 'Human verification timed out'),
  };
  const ready = phase === 'ready';
  const refreshLabel = text('换一张图片', 'Refresh puzzle');
  const checkLabel = text('验证拼图', 'Check puzzle');
  const imageError = () => {
    generation.current++;
    pending.current = false;
    notify.current('');
    setExpires(0);
    setPhase('error');
  };
  const dragMove = (event) => {
    if (!drag.current || drag.current.id !== event.pointerId) return;
    move(
      drag.current.start +
        ((event.clientX - drag.current.x) * 320) / drag.current.width
    );
  };

  return (
    <div
      className={`registration-puzzle-v108${
        expired ? ' registration-puzzle-v108--expired' : ''
      }`}
      role='group'
      aria-label={text('人机验证', 'Human verification')}
      aria-busy={phase === 'loading' || phase === 'checking'}
    >
      <div className='registration-puzzle-v108__header'>
        <span role='status' aria-live='polite'>
          <Icon
            name={
              phase === 'verified'
                ? 'check circle'
                : expired
                ? 'warning circle'
                : rotating
                ? 'redo'
                : 'puzzle piece'
            }
          />
          {labels[phase]}
        </span>
        <Button
          type='button'
          basic
          icon='refresh'
          size='small'
          title={refreshLabel}
          aria-label={refreshLabel}
          disabled={phase === 'loading' || phase === 'checking'}
          onClick={() => setRefresh((value) => value + 1)}
        />
      </div>
      <div className='registration-puzzle-v108__picture' ref={picture}>
        {puzzle && (
          <>
            <img
              className='registration-puzzle-v108__background'
              src={puzzle.background}
              alt={text('拼图背景', 'Puzzle background')}
              draggable={false}
              onError={imageError}
            />
            <img
              className='registration-puzzle-v108__piece'
              src={puzzle.piece}
              alt=''
              draggable={false}
              onError={imageError}
              style={{
                left: `${
                  ((rotating ? puzzle.piece_x : position) / 320) * 100
                }%`,
                top: `${(puzzle.piece_y / 180) * 100}%`,
                width: `${(puzzle.piece_size / 320) * 100}%`,
                transform: rotating ? `rotate(${position}deg)` : undefined,
                pointerEvents: rotating ? 'none' : undefined,
                cursor: ready && !rotating ? 'grab' : 'default',
              }}
              onPointerDown={(event) => {
                if (!ready || rotating || event.button !== 0) return;
                event.preventDefault();
                event.currentTarget.setPointerCapture(event.pointerId);
                drag.current = {
                  id: event.pointerId,
                  x: event.clientX,
                  start: currentPosition.current,
                  width: picture.current.getBoundingClientRect().width,
                };
              }}
              onPointerMove={dragMove}
              onPointerUp={(event) => {
                if (drag.current?.id !== event.pointerId) return;
                dragMove(event);
                drag.current = null;
                verify();
              }}
              onPointerCancel={() => {
                drag.current = null;
              }}
            />
            <span className='registration-puzzle-v108__credit'>NASA / JSC</span>
          </>
        )}
        {phase === 'loading' && <Icon loading name='spinner' size='large' />}
        {phase === 'verified' && (
          <div className='registration-puzzle-v108__verified'>
            <Icon name='check circle' size='big' />
          </div>
        )}
        {expired && (
          <div className='registration-puzzle-v108__expired'>
            <p role='alert'>{expiryMessage}</p>
            <Button
              type='button'
              className='registration-puzzle-v108__retry'
              onClick={() => setRefresh((value) => value + 1)}
            >
              <Icon name='refresh' />
              {retryLabel}
            </Button>
          </div>
        )}
      </div>
      <div className='registration-puzzle-v108__controls'>
        <input
          type='range'
          min='0'
          max={rotating ? 360 : 256}
          step='1'
          value={position}
          aria-label={
            rotating
              ? text('旋转角度', 'Rotation angle')
              : text('拼图位置', 'Puzzle position')
          }
          aria-valuetext={rotating ? `${position}\u00b0` : undefined}
          disabled={!ready}
          onChange={(event) => move(Number(event.target.value))}
          onPointerUp={() => verify()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              verify();
            }
          }}
        />
        {rotating && (
          <output
            className='registration-puzzle-v108__angle'
            aria-hidden='true'
          >
            {position}
            {'\u00b0'}
          </output>
        )}
        <Button
          type='button'
          icon='check'
          basic
          disabled={!ready}
          aria-label={checkLabel}
          title={checkLabel}
          onClick={verify}
        />
      </div>
    </div>
  );
}
